import { useEffect, useState, useCallback } from 'react'
import {
  GraduationCap, BookOpen, Award, Star, PlayCircle, FileText, CheckCircle2, Plus, X,
  ChevronLeft, Clock, BarChart3, Trash2, Pencil, Video, Route, Search, Eye, Download,
} from 'lucide-react'
import api from '../api/client'
import Loader from '../components/Loader'
import { successModal, errorModal, confirmModal } from '../components/ui'

function load(key) { try { return JSON.parse(localStorage.getItem(key) || 'null') } catch { return null } }

function embedUrl(url) {
  if (!url) return null
  const yt = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([\w-]{11})/)
  if (yt) return `https://www.youtube.com/embed/${yt[1]}`
  const vm = url.match(/vimeo\.com\/(\d+)/)
  if (vm) return `https://player.vimeo.com/video/${vm[1]}`
  return url
}
const isFile = (url) => url && (url.startsWith('data:') || /\.(mp4|webm|ogg)(\?|$)/i.test(url))

export default function Universidade() {
  const isAdmin = (load('user')?.perfil || '') === 'admin'
  const [tab, setTab] = useState('cursos')
  const [cursos, setCursos] = useState([])
  const [trilhas, setTrilhas] = useState([])
  const [painel, setPainel] = useState(null)
  const [certs, setCerts] = useState([])
  const [stats, setStats] = useState(null)
  const [sel, setSel] = useState(null)
  const [loading, setLoading] = useState(true)
  const [editCurso, setEditCurso] = useState(null)   // objeto p/ modal admin
  const [cats, setCats] = useState([])
  const [busca, setBusca] = useState('')
  const [catSel, setCatSel] = useState('')

  useEffect(() => { api.get('/universidade/categorias').then((r) => setCats(r.data.categorias || [])).catch(() => {}) }, [])

  const carregar = useCallback(() => {
    setLoading(true)
    const ps = [
      api.get('/universidade/cursos').then((r) => setCursos(r.data.data || [])).catch(() => setCursos([])),
      api.get('/universidade/trilhas').then((r) => setTrilhas(r.data.data || [])).catch(() => setTrilhas([])),
    ]
    if (isAdmin) ps.push(api.get('/universidade/stats').then((r) => setStats(r.data)).catch(() => {}))
    else {
      ps.push(api.get('/universidade/meu-painel').then((r) => setPainel(r.data)).catch(() => {}))
      ps.push(api.get('/universidade/certificados').then((r) => setCerts(r.data.data || [])).catch(() => {}))
    }
    Promise.all(ps).finally(() => setLoading(false))
  }, [isAdmin])
  useEffect(() => { carregar() }, [carregar])

  const abrir = (id) => api.get(`/universidade/cursos/${id}`).then((r) => setSel(r.data)).catch(() => errorModal('Curso indisponível.'))

  const cursosFiltrados = cursos.filter((c) => {
    if (catSel && c.categoria !== catSel) return false
    if (busca) { const t = busca.toLowerCase(); return (c.titulo || '').toLowerCase().includes(t) || (c.descricao || '').toLowerCase().includes(t) || (c.categoria || '').toLowerCase().includes(t) }
    return true
  })

  if (sel) return <CursoDetalhe curso={sel} isAdmin={isAdmin} onVoltar={() => { setSel(null); carregar() }} onReload={() => abrir(sel.id)} />

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-400 to-violet-500 flex items-center justify-center shadow"><GraduationCap size={20} className="text-white" /></div>
          <div>
            <h1 className="text-2xl font-heading font-bold text-gray-900">Universidade Corporativa</h1>
            <p className="text-sm text-gray-500">{isAdmin ? 'Gerencie cursos, trilhas e acompanhe o engajamento da rede.' : 'Treinamentos, videoaulas e materiais para você crescer.'}</p>
          </div>
        </div>
        {isAdmin && <button onClick={() => setEditCurso({})} className="flex items-center gap-2 bg-gray-900 text-white px-4 py-2 rounded-xl text-sm font-semibold hover:bg-gray-800"><Plus size={16} /> Novo Curso</button>}
      </div>

      {!isAdmin && painel && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <MiniCard icon={BookOpen} label="Em andamento" value={painel.em_andamento?.length || 0} color="from-blue-400 to-cyan-400" />
          <MiniCard icon={CheckCircle2} label="Concluídos" value={painel.concluidos?.length || 0} color="from-emerald-400 to-teal-400" />
          <MiniCard icon={Award} label="Certificados" value={painel.certificados || 0} color="from-amber-400 to-orange-400" />
          <MiniCard icon={Star} label="Favoritos" value={painel.favoritos || 0} color="from-violet-400 to-purple-400" />
        </div>
      )}
      {isAdmin && stats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <MiniCard icon={BookOpen} label="Cursos" value={stats.total_cursos} color="from-indigo-400 to-violet-400" />
          <MiniCard icon={Video} label="Aulas" value={stats.total_aulas} color="from-blue-400 to-cyan-400" />
          <MiniCard icon={Award} label="Certificados emitidos" value={stats.certificados_emitidos} color="from-amber-400 to-orange-400" />
          <MiniCard icon={BarChart3} label="Cursos mais acessados" value={stats.mais_acessados?.length || 0} color="from-emerald-400 to-teal-400" />
        </div>
      )}

      {!isAdmin && painel && (painel.novos?.length || painel.mais_assistidos?.length || painel.ultimos_acessados?.length) ? (
        <div className="grid md:grid-cols-3 gap-3">
          <DestaqueLista titulo="Novos materiais" cor="text-emerald-600" itens={painel.novos} onAbrir={abrir} vazio="Sem novidades" />
          <DestaqueLista titulo="Mais assistidos" cor="text-violet-600" itens={painel.mais_assistidos} onAbrir={abrir} vazio="Sem dados" />
          <DestaqueLista titulo="Últimos acessados" cor="text-blue-600" itens={painel.ultimos_acessados} onAbrir={abrir} vazio="Você ainda não acessou cursos" />
        </div>
      ) : null}

      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex gap-1.5">
          {[['cursos', 'Cursos', BookOpen], ['trilhas', 'Trilhas', Route], ...(isAdmin ? [['relatorios', 'Relatórios', BarChart3]] : [['certs', 'Certificados', Award]])].map(([k, label, Icon]) => (
            <button key={k} onClick={() => setTab(k)} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold ${tab === k ? 'bg-gray-900 text-white' : 'bg-white border border-gray-200 text-gray-600'}`}><Icon size={14} /> {label}</button>
          ))}
        </div>
        {tab === 'cursos' && (
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
              <input value={busca} onChange={(e) => setBusca(e.target.value)} placeholder="Buscar curso..." className="pl-8 pr-3 py-1.5 text-sm border border-gray-200 rounded-lg w-44" />
            </div>
            <select value={catSel} onChange={(e) => setCatSel(e.target.value)} className="border border-gray-200 rounded-lg px-2 py-1.5 text-sm">
              <option value="">Todas categorias</option>
              {cats.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
        )}
      </div>

      {loading ? <div className="flex justify-center py-16"><Loader text="Carregando..." /></div> : (
        <>
          {tab === 'cursos' && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {cursosFiltrados.map((c) => (
                <div key={c.id} className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-md transition cursor-pointer" onClick={() => abrir(c.id)}>
                  <div className="h-32 bg-gradient-to-br from-indigo-500 to-violet-600 relative flex items-center justify-center">
                    {c.capa_url ? <img src={c.capa_url} alt="" className="w-full h-full object-cover" /> : <GraduationCap size={40} className="text-white/80" />}
                    {c.obrigatorio && <span className="absolute top-2 right-2 bg-red-500 text-white text-[10px] px-2 py-0.5 rounded-full font-bold">Obrigatório</span>}
                  </div>
                  <div className="p-4 space-y-2">
                    <div className="flex items-center gap-2 text-[11px] text-gray-400">
                      {c.categoria && <span className="bg-gray-100 px-2 py-0.5 rounded-full">{c.categoria}</span>}
                      {c.nivel && <span>{c.nivel}</span>}
                      {c.carga_horaria ? <span className="flex items-center gap-0.5"><Clock size={10} /> {Math.round(c.carga_horaria / 60) || c.carga_horaria}h</span> : null}
                    </div>
                    <h3 className="font-heading font-bold text-gray-900 leading-tight">{c.titulo}</h3>
                    {c.instrutor && <p className="text-xs text-gray-500">{c.instrutor}</p>}
                    <div>
                      <div className="flex justify-between text-[11px] text-gray-400 mb-0.5"><span>{c.aulas_concluidas}/{c.aulas_total} aulas</span><span>{c.progresso}%</span></div>
                      <div className="w-full h-1.5 bg-gray-100 rounded-full overflow-hidden"><div className="h-full bg-gradient-to-r from-indigo-400 to-violet-500" style={{ width: `${c.progresso}%` }} /></div>
                    </div>
                  </div>
                </div>
              ))}
              {cursosFiltrados.length === 0 && <p className="text-gray-400 text-sm col-span-full py-10 text-center">Nenhum curso encontrado.{isAdmin ? ' Clique em "Novo Curso".' : ''}</p>}
            </div>
          )}

          {tab === 'trilhas' && (
            <div className="space-y-3">
              {isAdmin && <NovaTrilha onCriada={carregar} />}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {trilhas.map((t) => (
                  <div key={t.id} className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100 flex items-center gap-3">
                    <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center"><Route size={20} className="text-white" /></div>
                    <div className="flex-1"><h3 className="font-semibold text-gray-900">{t.nome}</h3><p className="text-xs text-gray-500">{t.descricao || `${cursos.filter((c) => c.trilha_id === t.id).length} curso(s)`}</p></div>
                  </div>
                ))}
                {trilhas.length === 0 && <p className="text-gray-400 text-sm py-6">Nenhuma trilha cadastrada.</p>}
              </div>
            </div>
          )}

          {tab === 'certs' && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {certs.map((c) => (
                <div key={c.id} className="bg-white rounded-2xl p-4 shadow-sm border border-amber-100 flex items-center gap-3">
                  <Award size={36} className="text-amber-500" />
                  <div><h3 className="font-semibold text-gray-900">{c.nome_curso}</h3><p className="text-xs text-gray-500">{c.nome_aluno} · {c.carga_horaria ? Math.round(c.carga_horaria / 60) + 'h · ' : ''}cód. {c.codigo}</p></div>
                </div>
              ))}
              {certs.length === 0 && <p className="text-gray-400 text-sm py-6">Conclua um curso 100% para emitir seu certificado.</p>}
            </div>
          )}

          {tab === 'relatorios' && stats && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                <MiniCard icon={Eye} label="Visualizações totais" value={stats.total_visualizacoes ?? 0} color="from-indigo-400 to-violet-400" />
                <MiniCard icon={Download} label="Downloads totais" value={stats.total_downloads ?? 0} color="from-blue-400 to-cyan-400" />
                <MiniCard icon={CheckCircle2} label="Taxa de conclusão" value={`${stats.taxa_conclusao ?? 0}%`} color="from-emerald-400 to-teal-400" />
                <MiniCard icon={Clock} label="Tempo médio assistido" value={`${stats.tempo_medio_assistido_min ?? 0} min`} color="from-amber-400 to-orange-400" />
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4">
                  <h3 className="font-semibold text-gray-900 mb-2">Cursos mais acessados</h3>
                  <table className="w-full text-sm">
                    <tbody>
                      {(stats.mais_acessados || []).map((m) => (
                        <tr key={m.curso_id} className="border-t border-gray-100"><td className="py-2">{m.titulo}</td><td className="py-2 text-right text-gray-500">{m.interacoes} interações</td></tr>
                      ))}
                      {(stats.mais_acessados || []).length === 0 && <tr><td className="py-6 text-center text-gray-400">Sem dados de acesso ainda.</td></tr>}
                    </tbody>
                  </table>
                </div>
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4">
                  <h3 className="font-semibold text-gray-900 mb-2">Licenciados que concluíram treinamentos</h3>
                  <div className="space-y-1 text-sm max-h-72 overflow-y-auto">
                    {(stats.concluintes || []).map((c, i) => (
                      <div key={i} className="flex items-center justify-between border-b border-gray-50 py-1">
                        <span className="text-gray-700">{c.nome_aluno}</span>
                        <span className="text-gray-400 text-xs">{c.nome_curso}</span>
                      </div>
                    ))}
                    {(stats.concluintes || []).length === 0 && <p className="text-gray-400 text-center py-6">Nenhum certificado emitido ainda.</p>}
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {editCurso && <CursoForm curso={editCurso} trilhas={trilhas} cats={cats} onClose={() => setEditCurso(null)} onSalvo={() => { setEditCurso(null); carregar() }} />}
    </div>
  )
}

function MiniCard({ icon: Icon, label, value, color }) {
  return (
    <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
      <div className={`w-9 h-9 rounded-xl bg-gradient-to-br ${color} flex items-center justify-center shadow mb-2`}><Icon size={16} className="text-white" /></div>
      <p className="text-xl font-heading font-bold text-gray-900">{value}</p>
      <p className="text-xs text-gray-500 mt-0.5">{label}</p>
    </div>
  )
}

function DestaqueLista({ titulo, cor, itens, onAbrir, vazio }) {
  return (
    <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
      <h3 className={`font-heading font-bold text-sm mb-2 ${cor}`}>{titulo}</h3>
      <div className="space-y-1.5">
        {(itens || []).slice(0, 5).map((c) => (
          <button key={c.id} onClick={() => onAbrir(c.id)} className="w-full text-left flex items-center gap-2 text-sm text-gray-700 hover:text-accent-600 group">
            <BookOpen size={14} className="text-gray-300 group-hover:text-accent-500 flex-shrink-0" />
            <span className="truncate flex-1">{c.titulo}</span>
            {c.obrigatorio && <span className="text-[9px] font-bold bg-red-100 text-red-600 px-1.5 rounded-full">OBR</span>}
          </button>
        ))}
        {(!itens || itens.length === 0) && <p className="text-xs text-gray-400 py-2">{vazio}</p>}
      </div>
    </div>
  )
}

function NovaTrilha({ onCriada }) {
  const [nome, setNome] = useState('')
  const criar = () => { if (!nome.trim()) return; api.post('/universidade/trilhas', { nome }).then(() => { setNome(''); onCriada() }).catch(() => errorModal('Erro.')) }
  return (
    <div className="bg-white rounded-2xl p-3 shadow-sm border border-gray-100 flex gap-2">
      <input value={nome} onChange={(e) => setNome(e.target.value)} placeholder="Nova trilha de aprendizagem..." className="flex-1 border border-gray-200 rounded-xl px-3 py-2 text-sm" />
      <button onClick={criar} className="bg-gray-900 text-white px-4 rounded-xl text-sm font-semibold">Criar</button>
    </div>
  )
}

function CursoForm({ curso, trilhas, cats = [], onClose, onSalvo }) {
  const ed = !!curso.id
  const [f, setF] = useState({
    titulo: curso.titulo || '', descricao: curso.descricao || '', categoria: curso.categoria || '',
    instrutor: curso.instrutor || '', nivel: curso.nivel || 'iniciante', carga_horaria: curso.carga_horaria || 0,
    trilha_id: curso.trilha_id || '', obrigatorio: curso.obrigatorio || false, capa_url: curso.capa_url || '',
    perfis: (curso.acesso_regra?.perfis || []).join(','),
    arquivado: curso.arquivado || false,
    publicar_em: (curso.publicar_em || '').slice(0, 16), expira_em: (curso.expira_em || '').slice(0, 16),
  })
  const [saving, setSaving] = useState(false)
  const set = (k) => (e) => setF({ ...f, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value })
  const salvar = () => {
    if (!f.titulo.trim()) return errorModal('Informe o título.')
    setSaving(true)
    const body = {
      ...f, carga_horaria: Number(f.carga_horaria) || 0,
      trilha_id: f.trilha_id ? Number(f.trilha_id) : null,
      acesso_regra: f.perfis.trim() ? { perfis: f.perfis.split(',').map((s) => s.trim()).filter(Boolean) } : null,
      publicar_em: f.publicar_em || null, expira_em: f.expira_em || null,
    }
    delete body.perfis
    const req = ed ? api.put(`/universidade/cursos/${curso.id}`, body) : api.post('/universidade/cursos', body)
    req.then(() => { successModal('Curso salvo.'); onSalvo() }).catch(() => errorModal('Erro ao salvar.')).finally(() => setSaving(false))
  }
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-lg p-5 space-y-3 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between"><h2 className="text-lg font-heading font-bold">{ed ? 'Editar' : 'Novo'} Curso</h2><button onClick={onClose}><X size={18} className="text-gray-400" /></button></div>
        <input value={f.titulo} onChange={set('titulo')} placeholder="Título *" className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm" />
        <textarea value={f.descricao} onChange={set('descricao')} rows={2} placeholder="Descrição" className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm" />
        <div className="grid grid-cols-2 gap-2">
          <select value={f.categoria} onChange={set('categoria')} className="border border-gray-200 rounded-xl px-3 py-2 text-sm">
            <option value="">Categoria...</option>
            {cats.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <input value={f.instrutor} onChange={set('instrutor')} placeholder="Instrutor" className="border border-gray-200 rounded-xl px-3 py-2 text-sm" />
          <select value={f.nivel} onChange={set('nivel')} className="border border-gray-200 rounded-xl px-3 py-2 text-sm"><option value="iniciante">Iniciante</option><option value="intermediario">Intermediário</option><option value="avancado">Avançado</option></select>
          <input type="number" value={f.carga_horaria} onChange={set('carga_horaria')} placeholder="Carga (min)" className="border border-gray-200 rounded-xl px-3 py-2 text-sm" />
          <select value={f.trilha_id} onChange={set('trilha_id')} className="border border-gray-200 rounded-xl px-3 py-2 text-sm"><option value="">Sem trilha</option>{trilhas.map((t) => <option key={t.id} value={t.id}>{t.nome}</option>)}</select>
          <input value={f.capa_url} onChange={set('capa_url')} placeholder="URL da capa" className="border border-gray-200 rounded-xl px-3 py-2 text-sm" />
        </div>
        <input value={f.perfis} onChange={set('perfis')} placeholder="Acesso por perfil (ex.: licenciado,admin) — vazio = todos" className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm" />
        <div className="grid grid-cols-2 gap-2">
          <label className="text-[11px] text-gray-400">Publicar em<input type="datetime-local" value={f.publicar_em} onChange={set('publicar_em')} className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm" /></label>
          <label className="text-[11px] text-gray-400">Expira em<input type="datetime-local" value={f.expira_em} onChange={set('expira_em')} className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm" /></label>
        </div>
        <div className="flex items-center gap-4">
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.obrigatorio} onChange={set('obrigatorio')} /> Curso obrigatório</label>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.arquivado} onChange={set('arquivado')} /> Arquivado</label>
        </div>
        <button onClick={salvar} disabled={saving} className="w-full bg-gray-900 text-white py-2.5 rounded-xl text-sm font-semibold disabled:opacity-50">{saving ? 'Salvando...' : 'Salvar Curso'}</button>
      </div>
    </div>
  )
}

function CursoDetalhe({ curso, isAdmin, onVoltar, onReload }) {
  const aulas = curso.aulas || []
  const [atual, setAtual] = useState(aulas.find((a) => !a.concluida) || aulas[0] || null)
  const [addOpen, setAddOpen] = useState(false)

  const marcar = (concluida) => api.post(`/universidade/aulas/${atual.id}/progresso`, { concluida }).then((r) => {
    if (r.data?.certificado_emitido) successModal('Parabéns! Curso concluído e certificado emitido. 🎓')
    onReload()
  }).catch(() => {})
  const favoritar = () => api.post(`/universidade/aulas/${atual.id}/progresso`, { favorito: !atual.favorito }).then(onReload).catch(() => {})
  const delAula = async (id) => { if (await confirmModal('Excluir esta aula?')) api.delete(`/universidade/aulas/${id}`).then(onReload).catch(() => errorModal('Erro.')) }

  const url = atual?.video_url
  return (
    <div className="space-y-3">
      <button onClick={onVoltar} className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-800"><ChevronLeft size={16} /> Voltar aos cursos</button>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 space-y-3">
          <div className="bg-black rounded-2xl overflow-hidden aspect-video flex items-center justify-center">
            {!atual ? <p className="text-white/50 text-sm">Sem aulas neste curso.</p>
              : atual.tipo === 'pdf' || atual.pdf_url ? <iframe title="pdf" src={atual.pdf_url} className="w-full h-full bg-white" />
              : atual.tipo === 'texto' ? <div className="text-white/90 p-6 overflow-y-auto text-sm whitespace-pre-wrap w-full h-full">{atual.conteudo}</div>
              : isFile(url) ? <video src={url} controls className="w-full h-full" />
              : url ? <iframe title="video" src={embedUrl(url)} allowFullScreen className="w-full h-full" />
              : <PlayCircle size={48} className="text-white/40" />}
          </div>
          {atual && (
            <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
              <h2 className="font-heading font-bold text-lg text-gray-900">{atual.titulo}</h2>
              {atual.descricao && <p className="text-sm text-gray-600 mt-1">{atual.descricao}</p>}
              {!isAdmin && (
                <div className="flex items-center gap-2 mt-3">
                  <button onClick={() => marcar(!atual.concluida)} className={`flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-semibold ${atual.concluida ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-900 text-white'}`}><CheckCircle2 size={16} /> {atual.concluida ? 'Concluída' : 'Marcar como concluída'}</button>
                  <button onClick={favoritar} className={`p-2 rounded-xl border ${atual.favorito ? 'bg-amber-50 border-amber-200 text-amber-500' : 'border-gray-200 text-gray-400'}`}><Star size={16} fill={atual.favorito ? 'currentColor' : 'none'} /></button>
                </div>
              )}
            </div>
          )}
        </div>
        <div className="space-y-2">
          <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
            <h3 className="font-heading font-bold text-gray-900">{curso.titulo}</h3>
            <div className="flex justify-between text-[11px] text-gray-400 mt-2 mb-0.5"><span>{curso.aulas_concluidas}/{curso.aulas_total} aulas</span><span>{curso.progresso}%</span></div>
            <div className="w-full h-1.5 bg-gray-100 rounded-full overflow-hidden"><div className="h-full bg-gradient-to-r from-indigo-400 to-violet-500" style={{ width: `${curso.progresso}%` }} /></div>
          </div>
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 divide-y divide-gray-100 max-h-[60vh] overflow-y-auto">
            {aulas.map((a, i) => (
              <button key={a.id} onClick={() => setAtual(a)} className={`w-full text-left px-3 py-2.5 flex items-center gap-2 hover:bg-gray-50 ${atual?.id === a.id ? 'bg-indigo-50' : ''}`}>
                {a.concluida ? <CheckCircle2 size={16} className="text-emerald-500 shrink-0" /> : a.tipo === 'pdf' ? <FileText size={16} className="text-gray-400 shrink-0" /> : <PlayCircle size={16} className="text-gray-400 shrink-0" />}
                <span className="flex-1 text-sm text-gray-700">{i + 1}. {a.titulo}</span>
                {a.duracao_min ? <span className="text-[10px] text-gray-400">{a.duracao_min}min</span> : null}
                {isAdmin && <Trash2 size={13} className="text-gray-300 hover:text-red-500" onClick={(e) => { e.stopPropagation(); delAula(a.id) }} />}
              </button>
            ))}
            {aulas.length === 0 && <p className="px-3 py-6 text-center text-gray-400 text-sm">Sem aulas.</p>}
            {isAdmin && <button onClick={() => setAddOpen(true)} className="w-full px-3 py-2.5 text-sm text-accent-600 font-semibold flex items-center gap-1.5 hover:bg-gray-50"><Plus size={15} /> Adicionar aula</button>}
          </div>
        </div>
      </div>
      {addOpen && <AulaForm cursoId={curso.id} onClose={() => setAddOpen(false)} onSalvo={() => { setAddOpen(false); onReload() }} />}
    </div>
  )
}

function AulaForm({ cursoId, onClose, onSalvo }) {
  const [f, setF] = useState({ titulo: '', descricao: '', tipo: 'video', video_url: '', pdf_url: '', conteudo: '', duracao_min: 0, ordem: 0 })
  const [saving, setSaving] = useState(false)
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value })
  const salvar = () => {
    if (!f.titulo.trim()) return errorModal('Informe o título da aula.')
    setSaving(true)
    api.post(`/universidade/cursos/${cursoId}/aulas`, { ...f, duracao_min: Number(f.duracao_min) || 0 })
      .then(() => { successModal('Aula adicionada.'); onSalvo() }).catch(() => errorModal('Erro.')).finally(() => setSaving(false))
  }
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-lg p-5 space-y-3" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between"><h2 className="text-lg font-heading font-bold">Nova Aula</h2><button onClick={onClose}><X size={18} className="text-gray-400" /></button></div>
        <input value={f.titulo} onChange={set('titulo')} placeholder="Título da aula *" className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm" />
        <select value={f.tipo} onChange={set('tipo')} className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm"><option value="video">Vídeo</option><option value="pdf">PDF</option><option value="texto">Texto</option></select>
        {f.tipo === 'video' && <input value={f.video_url} onChange={set('video_url')} placeholder="URL do vídeo (YouTube/Vimeo/MP4)" className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm" />}
        {f.tipo === 'pdf' && <input value={f.pdf_url} onChange={set('pdf_url')} placeholder="URL do PDF" className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm" />}
        {f.tipo === 'texto' && <textarea value={f.conteudo} onChange={set('conteudo')} rows={4} placeholder="Conteúdo da aula" className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm" />}
        <div className="grid grid-cols-2 gap-2">
          <input type="number" value={f.duracao_min} onChange={set('duracao_min')} placeholder="Duração (min)" className="border border-gray-200 rounded-xl px-3 py-2 text-sm" />
          <input type="number" value={f.ordem} onChange={set('ordem')} placeholder="Ordem" className="border border-gray-200 rounded-xl px-3 py-2 text-sm" />
        </div>
        <textarea value={f.descricao} onChange={set('descricao')} rows={2} placeholder="Descrição (opcional)" className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm" />
        <button onClick={salvar} disabled={saving} className="w-full bg-gray-900 text-white py-2.5 rounded-xl text-sm font-semibold disabled:opacity-50">{saving ? 'Salvando...' : 'Adicionar Aula'}</button>
      </div>
    </div>
  )
}
